import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, Play, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { resolvePlaybook, welcomePlaybookIdForRole } from "./playbooks";
import { startPingfloTour } from "./tourEvents";
import "./PingfloManual.css";

export const OPEN_PINGFLO_MANUAL_EVENT = "smarttt:open-pingflo-manual";

const QA = [
  {
    id: "live",
    roles: ["player", "coach", "admin", "parent"],
    q: "How do I join a live training?",
    a: "Open Live Training from the home navigation, pick your table, and wait for the coach to start. You can also scan the table QR when the coach shares it.",
  },
  {
    id: "eval",
    roles: ["player", "coach", "admin"],
    q: "How do I review an evaluation video?",
    a: "Open the session result and play the clip. Use −3s / +3s and frame step to pause, then Knees or Elbows for angles. Stroke analysis and frame notes stay on that clip.",
  },
  {
    id: "championship",
    roles: ["player", "coach", "admin", "parent"],
    q: "Where are championship matches?",
    a: "Open Competition. The next match is the large card. Final and live scores sit on the top right of the next match and of each fixture.",
  },
  {
    id: "smart",
    roles: ["player", "coach", "parent"],
    q: "What is the SMART path?",
    a: "SMART is your training plan: objectives, exercises, and progress. Open it from your profile or the SMART entry Pingflo points to on the home screen.",
  },
  {
    id: "coach-session",
    roles: ["coach", "admin"],
    q: "How does a coach add a session?",
    a: "On the coach home, add a session for the group, set the date, then plan tables and exercises. Start Live Training when players are at the tables.",
  },
  {
    id: "rsvp",
    roles: ["player", "parent", "coach"],
    q: "How do I say if I can play a match?",
    a: "Open the championship fixture and set your presence. Parents can confirm availability for their child from the same match card.",
  },
];

export function openPingfloManual() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PINGFLO_MANUAL_EVENT));
}

export function PingfloManual({ role = "player" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("manual");

  useEffect(() => {
    const onOpen = () => {
      setTab("manual");
      setOpen(true);
    };
    window.addEventListener(OPEN_PINGFLO_MANUAL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PINGFLO_MANUAL_EVENT, onOpen);
  }, []);

  const chapters = useMemo(() => {
    const playbook = resolvePlaybook(welcomePlaybookIdForRole(role), { role });
    const steps = Array.isArray(playbook?.steps) ? playbook.steps : [];
    const seen = new Set();
    return steps.filter((step) => {
      const key = step.titleKey || step.id;
      if (!key || seen.has(key) || step.silent) return false;
      seen.add(key);
      return true;
    });
  }, [role]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="pingflo-manual">
      <button type="button" className="pingflo-manual__backdrop" aria-label={t("close", "Close")} onClick={() => setOpen(false)} />
      <div className="pingflo-manual__sheet" role="dialog" aria-modal="true" aria-labelledby="pingflo-manual-title">
        <header className="pingflo-manual__head">
          <h2 id="pingflo-manual-title">
            <BookOpen size={18} aria-hidden />
            {t("pingfloManual", "Pingflo manual")}
          </h2>
          <button type="button" onClick={() => setOpen(false)} aria-label={t("close", "Close")}>
            <X size={18} />
          </button>
        </header>
        <div className="pingflo-manual__tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "manual"} className={tab === "manual" ? "is-on" : ""} onClick={() => setTab("manual")}>
            {t("pingfloManualSteps", "Guided steps")}
          </button>
          <button type="button" role="tab" aria-selected={tab === "qa"} className={tab === "qa" ? "is-on" : ""} onClick={() => setTab("qa")}>
            {t("pingfloManualQa", "Q&A")}
          </button>
        </div>
        {tab === "manual" ? (
          <ol className="pingflo-manual__list">
            {chapters.map((step, index) => (
              <li key={step.id || step.titleKey}>
                <strong>
                  {index + 1}. {t(step.titleKey, step.titleKey)}
                </strong>
                <p>{t(step.bodyKey, "")}</p>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    startPingfloTour({
                      playbookId: welcomePlaybookIdForRole(role),
                      replay: true,
                      role,
                      startStepId: step.id,
                    });
                  }}
                >
                  <Play size={14} aria-hidden />
                  {t("pingfloManualReplay", "Show this step")}
                </button>
              </li>
            ))}
          </ol>
        ) : (
          <ul className="pingflo-manual__list">
            {QA.filter((item) => item.roles.includes(role)).map((item) => (
              <li key={item.id}>
                <strong>{t(`pingfloQa_${item.id}_q`, item.q)}</strong>
                <p>{t(`pingfloQa_${item.id}_a`, item.a)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body
  );
}

export default PingfloManual;
